import React, { useEffect, useState } from 'react';
import {
  SimpleGrid, Card, Text, Group, ThemeIcon, Loader, Alert,
  Badge, Stack, Table, Title, Box,
} from '@mantine/core';
import {
  MdBusiness, MdPrecisionManufacturing, MdPeople,
  MdWarning, MdStorefront, MdCheckCircle, MdRadioButtonUnchecked,
} from 'react-icons/md';
import { getOverview } from '../../API/adminAPI';

const ATOMS_BLUE = '#1F4E79';

const StatCard = ({ icon, label, value, sub, color = ATOMS_BLUE }) => (
  <Card withBorder radius="md" p="md" sx={{ borderLeft: `4px solid ${color}` }}>
    <Group position="apart" noWrap>
      <Stack spacing={2}>
        <Text size="xs" color="dimmed" weight={600} transform="uppercase">{label}</Text>
        <Text size="xl" weight={700} color={ATOMS_BLUE}>{value ?? '—'}</Text>
        {sub && <Text size="xs" color="dimmed">{sub}</Text>}
      </Stack>
      <ThemeIcon size={44} radius="md" variant="light" color="blue">
        {icon}
      </ThemeIcon>
    </Group>
  </Card>
);

const CheckItem = ({ done, label, hint }) => (
  <Group spacing={10} noWrap>
    {done
      ? <MdCheckCircle size={18} color="#40c057" style={{ flexShrink: 0 }} />
      : <MdRadioButtonUnchecked size={18} color="#adb5bd" style={{ flexShrink: 0 }} />}
    <Box>
      <Text size="sm" weight={done ? 400 : 600} color={done ? 'dimmed' : undefined}
        sx={{ textDecoration: done ? 'line-through' : 'none' }}>
        {label}
      </Text>
      {!done && hint && <Text size="xs" color="dimmed">{hint}</Text>}
    </Box>
  </Group>
);

const OverviewTab = () => {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  useEffect(() => {
    getOverview()
      .then(setData)
      .catch((e) => setError(e?.response?.data?.error || 'Failed to load overview'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Loader mt="xl" mx="auto" />;
  if (error) return <Alert color="red">{error}</Alert>;

  const roleBadge = {
    atoms_admin:       { color: 'violet', label: 'ATOMS Admin' },
    oem_admin:         { color: 'blue',   label: 'OEM Admin' },
    distributor_admin: { color: 'teal',   label: 'Dist. Admin' },
    user:              { color: 'gray',   label: 'User' },
  };

  // Setup checklist derived from real data
  const hasOem   = data.total_oems   > 0;
  const hasMach  = data.total_machines > 0;
  const hasUsers = data.total_users  > 1;  // more than just super-admin
  const hasDist  = data.total_dists  > 0;

  return (
    <Stack spacing="lg">
      <SimpleGrid cols={4} breakpoints={[{ maxWidth: 'sm', cols: 2 }]}>
        <StatCard icon={<MdBusiness size={22} />}            label="OEM Organisations" value={data.total_oems} />
        <StatCard icon={<MdStorefront size={22} />}          label="Distributors"       value={data.total_dists} />
        <StatCard
          icon={<MdPrecisionManufacturing size={22} />}
          label="Machines"
          value={data.total_machines}
          sub={`${data.active_machines} active (last 24h)`}
        />
        <StatCard
          icon={<MdPeople size={22} />}
          label="Users"
          value={data.total_users}
          sub={`${data.active_users} logged in`}
        />
      </SimpleGrid>

      {data.suspended_orgs > 0 && (
        <Alert icon={<MdWarning />} color="orange" title="Suspended Organisations">
          {data.suspended_orgs} organisation{data.suspended_orgs > 1 ? 's are' : ' is'} currently
          suspended. Their users cannot log in.
        </Alert>
      )}

      <SimpleGrid cols={2} breakpoints={[{ maxWidth: 'md', cols: 1 }]}>
        {/* Setup checklist */}
        <Card withBorder radius="md" p="md">
          <Title order={5} mb="sm" color={ATOMS_BLUE}>Setup Checklist</Title>
          <Stack spacing={10}>
            <CheckItem done label="Create ATOMS super-admin" />
            <CheckItem
              done={hasOem}
              label="Create an OEM organisation"
              hint="Go to Organisations tab → New Organisation → Type: OEM"
            />
            <CheckItem
              done={hasMach}
              label="Commission at least one machine"
              hint="Go to Machines tab → Commission Machine"
            />
            <CheckItem
              done={hasMach}
              label="Assign machine to an OEM"
              hint="Edit machine → set 'Owned By' to your OEM org"
            />
            <CheckItem
              done={hasUsers}
              label="Create OEM admin user"
              hint="Go to Users tab → New User → Role: OEM Admin"
            />
            <CheckItem
              done={hasDist}
              label="Create a Distributor organisation (optional)"
              hint="Go to Organisations tab → New Organisation → Type: Distributor"
            />
            <CheckItem
              done={hasDist && hasMach}
              label="Share machine(s) to distributor (optional)"
              hint="Organisations tab → Distributor row → Share icon"
            />
          </Stack>
        </Card>

        {/* Recent users */}
        <Card withBorder radius="md" p="md">
          <Title order={5} mb="sm" color={ATOMS_BLUE}>Recently Added Users</Title>
          {!data.recent_users?.length ? (
            <Text color="dimmed" size="sm">No users yet.</Text>
          ) : (
            <Table striped>
              <thead>
                <tr>
                  <th>Username</th>
                  <th>Organisation</th>
                  <th>Role</th>
                  <th>Joined</th>
                </tr>
              </thead>
              <tbody>
                {data.recent_users.map((u, i) => {
                  const rb = roleBadge[u['profile__role']] || roleBadge.user;
                  return (
                    <tr key={i}>
                      <td><Text size="sm" weight={500}>{u.username}</Text></td>
                      <td><Text size="sm" color="dimmed">{u['profile__organisation__name'] || '—'}</Text></td>
                      <td><Badge size="xs" color={rb.color} variant="light">{rb.label}</Badge></td>
                      <td><Text size="xs" color="dimmed">{new Date(u.date_joined).toLocaleDateString()}</Text></td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </Card>
      </SimpleGrid>
    </Stack>
  );
};

export default OverviewTab;
